import { useEffect, useState } from "react";

import { PalButton, PalIconButton, PalTooltip, PalTypography } from "@palamar/fe-library";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Info, PanoramaFishEye, Settings, TaskAlt } from "@mui/icons-material";
import { Box, Grid, lighten } from "@mui/material";

import { ReactComponent as FacebookMessengerIcon } from "~assets/images/icons/facebook-messenger.svg";
import { ReactComponent as InstagramIcon } from "~assets/images/icons/instagram.svg";
import { ReactComponent as TelegramIcon } from "~assets/images/icons/telegram.svg";
import { ReactComponent as WhatsAppIcon } from "~assets/images/icons/whatsapp.svg";
import IntegrationLoadingModal from "~common/modals/IntegrationLoadingModal";
import AlertHelper from "~helpers/AlertHelper";
import {
  updateFacebookMessengerOnboarding,
  updateInstagramOnboarding,
  updateWhatsappOnboarding,
} from "~store/onboarding/actions";
import { createOrUpdateChannel, getChannelList } from "~store/settings/channels/actions";
import { selectProject } from "~store/settings/projectSettings/selectors";

import {
  FacebookMessengerPageSelectModal,
  IntegrationDetailModal,
  ManualIntegrationModal,
  StepContainer,
} from "../CommonComponents";

export function IntegrationItem({ image: Image, name, desc, onClick, onClickConfigure, onClickDetail, isDone }) {
  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      spacing={1}
      sx={{
        userSelect: "none",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 1,
        padding: 2,
        height: 230,
        ...(isDone && {
          backgroundColor: (theme) => lighten(theme.palette.success.light, 0.91),
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            marginRight: "auto",
            alignItems: "anchor-center",
            svg: {
              width: 64,
              height: 64,
            },
          }}
        >
          <Image />

          <PalTypography fontWeight={600} ml={1} variant="h6">
            {name}
          </PalTypography>
        </Box>
        <Box>
          {isDone && <TaskAlt color="success" />}
          {!isDone && <PanoramaFishEye color="secondary" />}
        </Box>
      </Box>

      <PalTypography my={1} overflowY="auto" variant="body2">
        {desc}
      </PalTypography>
      <Box alignItems={"center"} display="flex" gap={1} mt={"auto"} width={1}>
        <PalTooltip {...(!isDone && { open: false })} title="Revoke the access from the integration's dashboard">
          <Box
            sx={{
              ...(isDone && {
                cursor: "not-allowed !important",
              }),
            }}
            width={1}
          >
            <PalButton
              fullWidth
              disabled={onClickConfigure ? false : isDone}
              sx={{
                flex: 1,

                // pointerEvents: isDone ? "none" : "auto",
                // opacity: isDone ? 0.5 : 1,
              }}
              onClick={onClickConfigure || onClick}
              color={onClickConfigure ? "primary" : !isDone ? "primary" : "error"}
              // disabled={isDone}
              {...(onClickConfigure && { startIcon: <Settings /> })}
              variant="outlined"
            >
              <PalTypography fontWeight={600} variant="body1">
                {onClickConfigure ? "Configure" : isDone ? "Disconnect" : "Connect"}
              </PalTypography>
            </PalButton>
          </Box>
        </PalTooltip>
        {isDone && (
          <PalTooltip title="Show integration details">
            <PalIconButton color="primary" sx={{ flex: "0 0 auto" }} variant="text" onClick={onClickDetail}>
              <Info />
            </PalIconButton>
          </PalTooltip>
        )}
      </Box>
    </Box>
  );
}

function StepChannels({ formik, stepKey }) {
  const { t } = useTranslation();
  const project = useSelector(selectProject);

  const dispatch = useDispatch();

  const [authState, setAuthState] = useState({
    whatsapp: false,
    Instagram: false,
    messenger: false,
  });

  useEffect(() => {
    const loadFacebookSDK = () => {
      if (document.getElementById("facebook-jssdk")) return;

      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;

      script.onload = () => {
        initializeFacebookSDK();
      };

      document.body.appendChild(script);
    };

    const initializeFacebookSDK = () => {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: import.meta.env.VITE_APP_FACEBOOK_APP_ID || "1427714218189046",
          autoLogAppEvents: true,
          xfbml: true,
          version: "v16.0",
        });
      };
    };

    loadFacebookSDK();
  }, []);

  const startWhatsappSignupFlow = async () => {
    if (!window.FB) {
      console.error("Facebook SDK not loaded yet.");
      return;
    }

    setAuthState({ ...authState, whatsapp: true });
    IntegrationLoadingModal.show({ permanent: true }).then(() => {
      setAuthState({ ...authState, whatsapp: false });
    });

    const fbPromise = new Promise((resolve) => {
      const tempCallback = (event) => {
        if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
          return;
        }
        try {
          const data = JSON.parse(event.data);
          if (data.type === "WA_EMBEDDED_SIGNUP") {
            if (data.event === "FINISH") {
              const { phone_number_id, waba_id } = data.data;
              // console.log("Phone number ID ", phone_number_id, " WhatsApp business account ID ", waba_id);
              window.removeEventListener("message", tempCallback);
              resolve({ phone_number_id, waba_id });
            } else if (data.event === "CANCEL") {
              // const { current_step } = data.data;
              // console.warn("Cancel at ", current_step);
              window.removeEventListener("message", tempCallback);
              resolve(null);
            } else if (data.event === "ERROR") {
              const { error_message } = data.data;
              console.error("error ", error_message);
              window.removeEventListener("message", tempCallback);
              resolve(null);
            }
          }
        } catch {}
      };
      window.addEventListener("message", tempCallback);
    });

    const loginPromise = new Promise((resolve) => {
      window.FB.login(
        function (response) {
          setAuthState({ ...authState, whatsapp: false });
          IntegrationLoadingModal.hide();
          if (response.authResponse) {
            resolve(response.authResponse.code);
          } else {
            console.error("Whatsapp login failed.");
            resolve(null);
          }
        },
        {
          config_id: "1101788468095291",
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "",
            sessionInfoVersion: "2",
          },
        }
      );
    });

    await Promise.all([fbPromise, loginPromise]).then(([fbData, accessToken]) => {
      const { phone_number_id, waba_id } = fbData;
      dispatch(
        updateWhatsappOnboarding(project?.id, {
          code: accessToken,
          phone_number_id,
          waba_id,
        })
      );
    });
    dispatch(getChannelList(project?.id));
  };
  const startFacebookMessengerSignupFlow = async () => {
    // await FacebookMessengerPageSelectModal.show({
    //   pages: [
    //     {
    //       id: "23412321321",
    //       name: "AAAAA-palmate",
    //       category: "VVVVVV' market",
    //     },
    //     {
    //       id: "534340979768488",
    //       name: "Test-palmate",
    //       category: "Farmers' market",
    //     },
    //   ],
    // });
    if (!window.FB) {
      console.error("Facebook SDK not loaded yet.");
      return;
    }

    setAuthState({ ...authState, messenger: true });
    IntegrationLoadingModal.show({ permanent: true }).then(() => {
      setAuthState({ ...authState, messenger: false });
    });

    const loginPromise = new Promise((resolve) => {
      window.FB.login(
        function (response) {
          setAuthState({ ...authState, messenger: false });
          IntegrationLoadingModal.hide();
          if (response.authResponse) {
            resolve(response.authResponse.code);
          } else {
            console.error("Whatsapp login failed.");
            resolve(null);
          }
        },
        {
          config_id: "927777429135954",
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "",
            sessionInfoVersion: "2",
          },
        }
      );
    });

    //   {
    //     "status": "success",
    //     "pages": [
    //         {
    //             "id": "534340979768488",
    //             "name": "Test-palmate",
    //             "category": "Farmers' market"
    //         }
    //     ]
    //   }

    const accessToken = await loginPromise;
    if (accessToken) {
      const response = await dispatch(
        updateFacebookMessengerOnboarding(project?.id, {
          action_type: "business_integration",
          code: accessToken,
        })
      );
      if (response?.status === "success") {
        if (!response?.pages) {
          AlertHelper.showWarning(t("common.errorIntegrationMessage.facebookMessenger.noPage"));

          return;
        } else {
          const selectedPage = await FacebookMessengerPageSelectModal.show({ pages: response.pages });
          if (!selectedPage) return;
          await dispatch(
            updateFacebookMessengerOnboarding(project?.id, {
              action_type: "page_integration",
              page_id: selectedPage.id,
            })
          );
        }
      } else {
        AlertHelper.showError(t("common.errorIntegrationMessage"));
      }

      dispatch(getChannelList(project?.id));
    }
  };
  const startInstagramLogin = async () => {
    if (window.INS_POPUP) {
      window.INS_POPUP.focus();
      return;
    }
    const clientId = import.meta.env.VITE_APP_INSTAGRAM_CLIENT_ID || "1848589722341734";
    const redirectUri = window.location.origin + import.meta.env.VITE_APP_PUBLIC_URL + "/instagram-callback/";

    const baseAddress = "https://www.instagram.com/oauth/authorize";
    const params = {
      enable_fb_login: 0,
      force_authentication: 1,
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope:
        "instagram_business_basic,instagram_business_manage_messages,instagram_business_manage_comments,instagram_business_content_publish,instagram_business_manage_insights",
    };

    const authUrl = `${baseAddress}?${new URLSearchParams(params).toString()}`;

    const popupWidth = 600;
    const popupHeight = 700;
    const left = window.screenX + (window.innerWidth - popupWidth) / 2;
    const top = window.screenY + (window.innerHeight - popupHeight) / 2;

    IntegrationLoadingModal.show().then(() => {
      setAuthState({ ...authState, Instagram: false });
      if (window.INS_POPUP && !window.INS_POPUP.closed) {
        window.INS_POPUP.close();
      }
    });
    setAuthState({ ...authState, Instagram: true });
    window.INS_POPUP = window.open(
      authUrl,
      "InstagramLogin",
      `width=${popupWidth},height=${popupHeight},top=${top},left=${left}`
    );

    const popupEventListener = async (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data?.code) {
        window.removeEventListener("message", popupEventListener);
        IntegrationLoadingModal.hide();
        // setIntegrationState({ ...integrationState, Instagram: event.data.code });
        // formik.setFieldValue("instagram.access_token", event.data.code);
        await dispatch(
          updateInstagramOnboarding(project?.id, {
            code: event.data.code,
          })
        );
        await dispatch(getChannelList(project?.id));
      }
    };
    window.addEventListener("message", popupEventListener);

    const interval = setInterval(() => {
      if (!window.INS_POPUP || window.INS_POPUP.closed) {
        clearInterval(interval);
        setAuthState({ ...authState, Instagram: false });
        IntegrationLoadingModal.hide();
        window.INS_POPUP = null;
      }
    }, 500);
  };
  const formikValues = formik.values?.[stepKey];

  const handleClickShowDetails = (integration) => () => {
    IntegrationDetailModal.show({ integration });
  };

  const handleManualIntegration = (integrationName) => async () => {
    let inputFields = [];
    let state = {};
    if (integrationName === "telegram") {
      state = formikValues?.telegram;
      inputFields = [
        {
          label: "Name",
          key: "name",
          required: true,
        },
        {
          label: "App ID",
          key: "app_id",
          required: true,
        },
        {
          label: "Access Token",
          key: "access_token",
          required: true,
        },
        {
          label: "Secret",
          key: "secret",
          required: true,
        },
      ];
    }

    const responseState = await ManualIntegrationModal.show({ integrationName, inputFields, state });
    if (responseState) {
      const newState = {
        id: state.id,
        ...responseState,
      };
      if (newState?.access_token && newState?.access_token === state?.access_token) {
        delete newState.access_token;
      }
      if (newState?.secret && newState?.secret === state?.secret) {
        delete newState.secret;
      }
      await dispatch(createOrUpdateChannel(project.id, newState));
      await dispatch(getChannelList(project.id));
    }
  };

  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            Channel Integrations
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            Please select the channels you would like to integrate with your assistant. This will allow your assistant
            to interact with users on these platforms.
          </PalTypography>
        </Grid>
        <Grid item xs={12}>
          <Box
            gap={3}
            sx={{
              borderRadius: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid item md={6} sm={12} xs={12}>
                <IntegrationItem
                  desc="Connect your Instagram account to chat with your customers."
                  image={InstagramIcon}
                  isDone={formikValues?.instagram?.access_token}
                  name="Instagram"
                  onClick={startInstagramLogin}
                  onClickDetail={handleClickShowDetails(formikValues?.instagram)}
                />
              </Grid>
              <Grid item md={6} sm={12} xs={12}>
                <IntegrationItem
                  desc="Connect your Facebook Messenger account to chat with your customers."
                  image={FacebookMessengerIcon}
                  isDone={formikValues?.messenger?.access_token}
                  name="Facebook Messenger"
                  onClick={startFacebookMessengerSignupFlow}
                  onClickDetail={handleClickShowDetails(formikValues?.messenger)}
                />
              </Grid>
              <Grid item md={6} sm={12} xs={12}>
                <IntegrationItem
                  desc="Connect your WhatsApp business account to communicate with your customers from your business number."
                  image={WhatsAppIcon}
                  isDone={formikValues?.whatsapp?.access_token}
                  name="WhatsApp"
                  onClick={startWhatsappSignupFlow}
                  onClickDetail={handleClickShowDetails(formikValues?.whatsapp)}
                />
              </Grid>
              <Grid item md={6} sm={12} xs={12}>
                <IntegrationItem
                  desc="Connect your Telegram account to chat with your customers."
                  image={TelegramIcon}
                  isDone={formikValues?.telegram?.access_token}
                  onClickConfigure={handleManualIntegration("telegram")}
                  name="Telegram"
                  // onClick={handleManualIntegration("telegram")}
                  onClickDetail={handleClickShowDetails(formikValues?.telegram)}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </StepContainer>
  );
}

export default StepChannels;
